import { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@loader/ui';

import {
  type ScheduleMode,
  type ParsedSchedule,
  HOURS,
  WEEKDAYS,
  MONTH_DAYS,
  TIMEZONE_OPTIONS,
  formatTimezoneLabel,
  parseSchedule,
  buildSchedule,
} from './schedulePickerUtils';

interface SchedulePickerProps {
  value: string | null;
  onChange: (schedule: string) => void;
}

const MODE_LABELS: Record<ScheduleMode, string> = {
  daily: 'Every day at',
  weekly: 'Every',
  monthly: 'Every',
  every_hour: '',
};

/** Underline-only select trigger: no border box, just a bottom line */
const triggerStyle = 'rounded-none border-0 border-b border-input shadow-none px-1';

export function SchedulePicker({ value, onChange }: SchedulePickerProps) {
  const parsed = useMemo(() => parseSchedule(value), [value]);

  const update = (overrides: Partial<ParsedSchedule>) => {
    onChange(buildSchedule({ ...parsed, ...overrides }));
  };

  return (
    <div className="space-y-3">
      {/* Row 1: Frequency */}
      <Tabs value={parsed.mode} onValueChange={(v) => update({ mode: v as ScheduleMode })}>
        <TabsList className="w-full">
          <TabsTrigger value="daily" className="flex-1">Daily</TabsTrigger>
          <TabsTrigger value="weekly" className="flex-1">Weekly</TabsTrigger>
          <TabsTrigger value="monthly" className="flex-1">Monthly</TabsTrigger>
          <TabsTrigger value="every_hour" className="flex-1">Hourly</TabsTrigger>
        </TabsList>
      </Tabs>

      {parsed.mode !== 'every_hour' && (
        <>
          {/* Row 2: label + [weekday/day] + at + time */}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground shrink-0 text-sm">{MODE_LABELS[parsed.mode]}</span>

            {parsed.mode === 'weekly' && (
              <>
                <Select value={parsed.weekday} onValueChange={(weekday) => update({ weekday })}>
                  <SelectTrigger className={`w-[120px] shrink-0 ${triggerStyle}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground shrink-0 text-sm">at</span>
              </>
            )}

            {parsed.mode === 'monthly' && (
              <>
                <Select value={String(parsed.monthDay)} onValueChange={(day) => update({ monthDay: Number(day) })}>
                  <SelectTrigger className={`w-[90px] shrink-0 ${triggerStyle}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_DAYS.map((d) => (
                      <SelectItem key={d} value={String(d)}>Day {d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-muted-foreground shrink-0 text-sm">at</span>
              </>
            )}

            <div>
              <Select value={parsed.hour} onValueChange={(hour) => update({ hour })}>
                <SelectTrigger className={`w-[70px] ${triggerStyle}`}>
                  <SelectValue>{parsed.hour}:00</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {HOURS.map((h) => (
                    <SelectItem key={h} value={h}>{h}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Timezone */}
          <Select value={parsed.timezone} onValueChange={(timezone) => update({ timezone })}>
            <SelectTrigger className={`w-auto ${triggerStyle}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONE_OPTIONS.map((tz) => (
                <SelectItem key={tz} value={tz}>{formatTimezoneLabel(tz)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}

      {parsed.mode === 'every_hour' && (
        <p className="text-muted-foreground text-sm">Syncs every hour.</p>
      )}

    </div>
  );
}
